import React, {
  ReactNode,
  ComponentProps,
  useEffect,
  useRef,
  useState,
} from 'react';

import PropTypes from 'prop-types';

import AnimateHeight from '@adsk/alloy-react-animate-height';
import { Text } from '@adsk/alloy-react-box';
import { LinkButton } from '@adsk/alloy-react-button';
import { useLocale } from '@adsk/alloy-react-locale';
import { useTheme } from '@adsk/alloy-react-styles';
import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';

type SeeMoreProps = StylableComponent<HTMLDivElement> & {
  text: ReactNode;
  collapsedStyle?: StylableComponent<HTMLSpanElement>['style'];
  linkButtonProps?: ComponentProps<typeof LinkButton>;
  detectUrls?: boolean;
  showUrlsIcons?: boolean;
};

const defaultCollapsedStyle = {
  display: '-webkit-box',
  overflow: 'hidden',
  '-webkit-box-orient': 'vertical',
  '-webkit-line-clamp': '3',
};

/**
 * SeeMore component. Allows expanding and collapsing long text.
 */
const SeeMore = React.forwardRef<HTMLDivElement, SeeMoreProps>(
  (
    {
      text,
      style,
      collapsedStyle,
      linkButtonProps = {},
      detectUrls = true,
      showUrlsIcons = true,
      ...props
    },
    ref,
  ) => {
    const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
    const seeMoreTokens = useTheme().tokens?.component?.activityLog?.seeMore;
    const { marginTop: showMoreButtonMarginTop, ...seeMoreTextTokens } =
      seeMoreTokens ?? {};
    const textRef = useRef<HTMLSpanElement>(null);
    const collapsedHeightRef = useRef<number>(0);
    const [hasEllipsis, setHasEllipsis] = useState(false);
    const [collapsing, setCollapsing] = useState(false);
    const [truncate, setTruncate] = useState(true);
    const {
      onClick: linkButtonOnClick,
      style: linkButtonStyle,
      ...restButtonProps
    } = linkButtonProps;

    useEffect(() => {
      if (
        textRef.current &&
        textRef.current.clientHeight < textRef.current.scrollHeight
      ) {
        setHasEllipsis(true);
        if (!collapsedHeightRef.current) {
          collapsedHeightRef.current = textRef.current.clientHeight;
        }
      }
    }, [text]);

    return (
      <div ref={ref} css={style} {...props}>
        <AnimateHeight
          isOpen={!hasEllipsis || !truncate}
          height={hasEllipsis ? collapsedHeightRef.current : undefined}
          onAnimationComplete={() => {
            setCollapsing(false);
          }}
        >
          <span
            ref={textRef}
            css={[
              seeMoreTextTokens,
              {
                whiteSpace: 'pre-line',
              },
              truncate && !collapsing && defaultCollapsedStyle,
              truncate && !collapsing && collapsedStyle,
            ]}
          >
            <Text detectUrls={detectUrls} showUrlsIcons={showUrlsIcons}>
              {text}
            </Text>
          </span>
        </AnimateHeight>
        {hasEllipsis && (
          <LinkButton
            style={[
              {
                alignSelf: 'start',
                marginTop: showMoreButtonMarginTop,
              },
              linkButtonStyle,
            ]}
            onClick={(event) => {
              if (!truncate) {
                setCollapsing(true);
              }
              setTruncate(!truncate);
              linkButtonOnClick?.(event);
            }}
            {...restButtonProps}
          >
            {truncate ? T.translate('SHOW_MORE') : T.translate('SHOW_LESS')}
          </LinkButton>
        )}
      </div>
    );
  },
);

SeeMore.displayName = 'SeeMore';

SeeMore.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Generic activity text to render */
  text: PropTypes.any.isRequired,
  /** Styles for collapsed state */
  collapsedStyle: stylePropType,
  /** Props passed to the show more link button */
  linkButtonProps: PropTypes.object,
  /** Props passed to the enble or disable the link detection */
  detectUrls: PropTypes.bool,
  /** Props passed to the show the links icon */
  showUrlsIcons: PropTypes.bool,
};

export default SeeMore;
